import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Text } from "@fluentui/react-components";
import { ArrowLeft24Regular, Save24Regular } from "@fluentui/react-icons";
import { useFilterStyles } from "../styles/filterStyles";
import SpreetailHeader from "../layout/SpreetailHeader";
import SpreetailLoader, { SpreetailSpinner } from "../common/SpreetailLoader";
import {
  saveTemplate,
  collectCurrentCalculatedColumns,
  getCurrentDashboardType,
  getUserTemplate,
} from "../../../shared/apiService";
import { config } from "../../../shared/config";
import { handleTokenExpiration } from "../../../shared/authUtils";
import { getPageSource } from "../../../shared/apiClient";

const API_BASE_URL = config.apiBaseUrl;

interface LocationState {
  mode?: "edit" | "new";
  existingTemplate?: {
    template_name: string;
    template_code: string;
    _id: string;
  };
}

interface Template {
  _id: string;
  template_name: string;
  template_code: string;
  version: string;
  dashboard_type: string;
}

const SaveTemplatesRoute: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const filterStyles = useFilterStyles();

  const [templateName, setTemplateName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [existingTemplate, setExistingTemplate] = useState<{
    exists: boolean;
    template_name?: string;
    template_id?: string;
    template_code?: string;
  } | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [currentDashboardType, setCurrentDashboardType] = useState<string>("");

  // Fetch templates and check for existing template on mount
  useEffect(() => {
    const initializeTemplates = async () => {
      try {
        setIsCheckingExisting(true);

        // Get current dashboard type
        const dashboardType = await getCurrentDashboardType();
        setCurrentDashboardType(dashboardType);

        // If coming from edit mode via location state
        if (location.state?.mode === "edit" && location.state?.existingTemplate) {
          setExistingTemplate({
            exists: true,
            template_name: location.state.existingTemplate.template_name,
            template_id: location.state.existingTemplate._id,
            template_code: location.state.existingTemplate.template_code,
          });
          setTemplateName(location.state.existingTemplate.template_name || "");
          setSelectedTemplateId(location.state.existingTemplate._id);
        } else {
          // Fetch all templates
          const token = await OfficeRuntime.storage.getItem("token");
          if (token) {
            const response = await fetch(`${API_BASE_URL}/templates`, {
              headers: {
                Authorization: `Bearer ${token}`,
                "X-Page-Source": getPageSource(),
              },
            });

            if (response.status === 401) {
              await handleTokenExpiration("Token expired during templates fetch");
            } else if (response.ok) {
              const allTemplates: Template[] = await response.json();
              // Filter templates for current dashboard type
              const filteredTemplates = allTemplates.filter(
                (t) => t.template_code === dashboardType || t.dashboard_type === dashboardType
              );
              setTemplates(filteredTemplates);
            }
          }

          // Check if user already has a template for current dashboard
          const existing = await getUserTemplate(dashboardType);
          setExistingTemplate(existing);

          // Pre-fill the template name if updating existing
          if (existing.exists && existing.template_name) {
            setTemplateName(existing.template_name);
            // Try to find and select the existing template in the dropdown
            if (existing.template_id) {
              setSelectedTemplateId(existing.template_id);
            }
          }
        }
      } catch (error: any) {
        console.error("Error initializing templates:", error);
        // Don't show error to user, just proceed as new template
        setExistingTemplate({ exists: false });
      } finally {
        setIsCheckingExisting(false);
      }
    };

    initializeTemplates();
  }, [location.state]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".template-dropdown-container")) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Handle template selection from dropdown
  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setDropdownOpen(false);

    if (templateId === "new") {
      // Create new template
      setTemplateName("");
      setExistingTemplate({ exists: false });
    } else {
      // Select existing template
      const template = templates.find((t) => t._id === templateId);
      if (template) {
        setTemplateName(template.template_name);
        setExistingTemplate({
          exists: true,
          template_name: template.template_name,
          template_id: template._id,
          template_code: template.template_code,
        });
      }
    }

    // Clear validation error when user selects a template
    if (validationError) {
      setValidationError(null);
    }
  };

  // Handle input change - when user types, treat as new template
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTemplateName(value);

    // If user is typing, check if it matches an existing template
    const matchingTemplate = templates.find((t) => t.template_name === value);
    if (matchingTemplate) {
      setSelectedTemplateId(matchingTemplate._id);
      setExistingTemplate({
        exists: true,
        template_name: matchingTemplate.template_name,
        template_id: matchingTemplate._id,
        template_code: matchingTemplate.template_code,
      });
    } else if (value.trim() === "") {
      setSelectedTemplateId("");
      setExistingTemplate({ exists: false });
    } else {
      // User is typing a new name
      setSelectedTemplateId("new");
      setExistingTemplate({ exists: false });
    }

    if (validationError) {
      setValidationError(null);
    }
  };

  const handleSave = async () => {
    // Validation: check if template name is entered
    if (!templateName.trim()) {
      setValidationError("Please enter a template name before saving.");
      return;
    }

    // Clear any previous validation errors
    setValidationError(null);

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Collect current dashboard data
      const calculatedColumns = await collectCurrentCalculatedColumns();
      const dashboardType = await getCurrentDashboardType();

      // Prepare template data
      const templateData = {
        template_code: dashboardType,
        calculated_columns: calculatedColumns,
        template_name: templateName,
      };

      // Save template via API
      const response = await saveTemplate(templateData);

      console.log("Template saved successfully:", response);

      // Show success message based on whether it was an update or new save
      const message = response.is_update
        ? "Template updated successfully!"
        : "Template saved successfully!";

      // Show success message to user and clear any errors
      setSuccessMessage(message);
      setError(null);
      
      // Update existing template state if it was an update
      if (response.is_update && response.template_id) {
        setExistingTemplate({
          exists: true,
          template_name: templateName,
          template_id: response.template_id,
          template_code: dashboardType,
        });
      } else if (!response.is_update) {
        // For new templates, mark as existing now
        setExistingTemplate({
          exists: true,
          template_name: templateName,
          template_id: response.template_id || "",
          template_code: dashboardType,
        });
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error: any) {
      console.error("Error saving template:", error);
      setError(error.message || "Failed to save template. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    history.push("/");
  };

  if (isCheckingExisting) {
    return (
      <div className={`${filterStyles.filterContainer} h-full min-h-0`}>
        <SpreetailHeader />
        <div className="flex justify-center items-center h-[200px]">
          <SpreetailLoader
            title="Loading templates"
            subtitle="Please wait while we check your existing templates"
            size="medium"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`${filterStyles.filterContainer} h-full min-h-0`}>
      <SpreetailHeader />
      <div className={filterStyles.filterHeader}>
        <Text size={400} weight="semibold" style={{ color: "#0F172A" }}>
          {existingTemplate?.exists ? "Update Template" : "Save Template"}
        </Text>
        <Text
          size={200}
          style={{ color: "#667085", opacity: 0.9, fontSize: "12px" }}
        >
          {existingTemplate?.exists
            ? "Update your existing dashboard template configuration"
            : "Save your current dashboard configuration as a reusable template"}
        </Text>
      </div>

      <div className={`${filterStyles.filterContent} px-4 pb-4`}>
        {existingTemplate?.exists && (
          <div className="bg-blue-100 rounded-md p-3 mb-4 flex items-start gap-2">
            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <p className="text-[13px] text-blue-800 m-0 leading-snug">
              You already have a saved template for this dashboard. Saving will update it.
            </p>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 rounded-md p-3 mb-4 flex items-start gap-2">
            <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-sm font-bold">✓</span>
            </div>
            <p className="text-[13px] text-green-800 m-0 leading-snug">{successMessage}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-100 rounded-md p-3 mb-4 flex items-start gap-2">
            <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <p className="text-[13px] text-red-800 m-0 leading-snug">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {/* Combined Template Dropdown + Text Input */}
          <div className="template-dropdown-container">
            <label
              htmlFor="template-name"
              className="block text-[13px] font-medium text-slate-900 mb-2"
            >
              Template Name
            </label>
            <div className="relative">
              <input
                id="template-name"
                type="text"
                placeholder="Enter template name or select from dropdown..."
                value={templateName}
                onChange={handleInputChange}
                onFocus={() => setDropdownOpen(true)}
                disabled={isLoading}
                className="w-full h-11 pl-3 pr-10 text-sm text-slate-900 bg-white border-[1.5px] border-gray-200 rounded-[10px] cursor-text outline-none focus:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setDropdownOpen(!dropdownOpen);
                }}
                disabled={isLoading}
                className="absolute right-0 top-0 h-11 w-10 flex items-center justify-center cursor-pointer border-none bg-transparent hover:bg-gray-50 rounded-r-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Show templates"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className={`transition-transform duration-200 ${dropdownOpen ? "rotate-180" : "rotate-0"}`}
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="#6B7280"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-md z-10 mt-1 max-h-48 overflow-y-auto">
                  {templates.length > 0 && (
                    <>
                      {templates.map((template, index) => (
                        <button
                          key={template._id}
                          type="button"
                          onClick={() => handleTemplateSelect(template._id)}
                          className="w-full p-2.5 px-3 border-none bg-transparent text-[13px] text-gray-800 text-left cursor-pointer hover:bg-gray-50 overflow-hidden"
                          style={{
                            borderBottom:
                              index < templates.length - 1 ? "1px solid #F3F4F6" : "none",
                            fontWeight: selectedTemplateId === template._id ? "600" : "400",
                            backgroundColor:
                              selectedTemplateId === template._id ? "#f3f4f6" : "transparent",
                          }}
                          title={`${template.template_name} (v${template.version})`}
                        >
                          <span className="truncate block">
                            {template.template_name} (v{template.version})
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                  {templates.length === 0 && (
                    <div className="p-2.5 px-3 text-[13px] text-gray-500 text-center">
                      No templates available
                    </div>
                  )}
                </div>
              )}
            </div>
            {validationError && (
              <div className="bg-red-100 rounded-md p-3 mt-2 flex items-start gap-2">
                <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">!</span>
                </div>
                <p className="text-[13px] text-red-800 m-0 leading-snug">{validationError}</p>
              </div>
            )}
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="w-full h-11 bg-[#068E75] text-white border-none rounded-[10px] text-sm font-medium cursor-pointer transition-colors duration-200 flex items-center justify-center gap-2 shadow-[0_2px_4px_rgba(6,142,117,0.3)] hover:bg-[#057A63] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? <SpreetailSpinner size="tiny" /> : <Save24Regular />}
            {isLoading
              ? "Saving..."
              : existingTemplate?.exists
                ? "Update Template"
                : "Save Template"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveTemplatesRoute;
